/**
 * Offline attendance wire contract v1 — the Syntry copy of the contract the
 * ERPat HumanResource ingestion endpoints implement
 * (`specs/offline-attendance-v1/implementation-plan.md`, "Shared wire contract
 * v1"; the backend half implements the same shapes).
 *
 * Four routes, all under `/v1/api/me/attendance/`: `offline-context`
 * (provisioning), `sync/state` (confirmed-snapshot refresh), `sync` (upload)
 * and `sync/events/{client_event_id}` (receipt lookup). This module owns the
 * TYPES of their `data` payloads, the result-code lists and the strict runtime
 * DECODERS every response passes through before the app trusts it:
 *
 *  - Unknown keys are ignored AND dropped: a decoder copies out only the keys
 *    the contract defines, so nothing unexpected is ever persisted.
 *  - A missing or mistyped required key fails the whole value with
 *    `{ ok: false, reason }`, where `reason` is the dotted path of the FIRST
 *    offending key (`grant.token`, `keys[0].x`, `receipt.outcome`) — or
 *    `"(root)"` when the value is not an object at all. Required nullable keys
 *    must be present (`null`, not absent). The only optional keys are
 *    `retry_after_ms` and, beside an OFF capability, `grant`, `keys` and
 *    `policy` (never read there) and the capability's `reason` (read
 *    leniently) — see below.
 *  - UUIDs (`client_event_id`, `receipt_id`, `grant_id`, `installation_id`,
 *    `sync_id`) are accepted in any case and returned lower-case. Decimal ids
 *    (`user_id`, `employee_id`, `attendance_id`) are accepted as a positive
 *    safe integer or a canonical positive decimal string (no sign, no leading
 *    zero, ≤ 20 digits) and returned as a string.
 *  - GRANT CLAIMS are the exception: ERPat compares them verbatim, so `uid` /
 *    `eid` must already be canonical decimal STRINGS and `gid` / `ins` already
 *    lower-case UUIDs — nothing is normalized (plan amendment C4). A claim
 *    normalized here would verify on the device and be refused at upload.
 *  - Cross-field rules are enforced too (a capability that is on carries a
 *    grant, a policy and keys; one open row names its attendance row and
 *    revision; an `accepted` receipt names the row and its revision…), each
 *    failing on the key that breaks it.
 *  - Where refusing would fail OPEN or lock an employee out, the decoder
 *    normalizes instead: an answer whose capability is OFF always decodes —
 *    the grant, policy and keys beside it are dropped (plan amendment C2) and
 *    a missing, null or unknown reason reads `not_ready` (FE-A2's extension of
 *    C2 from those fields to the reason) — because refusing it would leave a
 *    stored ENABLED context authorizing capture; and, so that one legacy
 *    attendance row cannot make every provisioning and state refresh
 *    undecodable, one open row may have an unknown clock-in time and a last
 *    closed shift that ends before it starts reads as none (plan amendment C3).
 *  - Two decoders also read data AT REST: `services/storage/attendance-storage.ts`
 *    re-decodes every confirmed snapshot and final receipt it stored when it
 *    reads one back. Within v1 those shapes may therefore only be LOOSENED —
 *    a stricter rule would turn what an older build stored into `corrupt`
 *    reads (see {@link decodeConfirmedSnapshot}, {@link decodeFinalReceipt}).
 *
 * Decoding is NOT settlement: a decoded sync result still has to be matched to
 * the event that was sent (id, type, time, scope echo, sync id) by the sync
 * engine before it may settle anything — HTTP 200 never implies acceptance.
 *
 * DELIBERATELY IMPORT-FREE, like `realtime-envelope-v1.ts`: the module can be
 * loaded straight into Node (native type stripping) by a fixture script, and
 * the golden fixtures beside it (`offline-attendance-v1.fixtures.json`) run
 * through these exact decoders in `src/test/offline/contract.spec.ts`.
 */

/** The only schema version this client speaks (request bodies and responses). */
export const OFFLINE_SCHEMA_VERSION = 1 as const;

/** The four explicit clock actions, in the order the UI lists them. */
export const ATTENDANCE_EVENT_TYPES = ['time_in', 'time_out', 'break_out', 'break_in'] as const;
export type AttendanceEventType = (typeof ATTENDANCE_EVENT_TYPES)[number];

/** Shift states a confirmed snapshot (and an event's `base`) can name. */
export const ATTENDANCE_SNAPSHOT_STATES = ['off_shift', 'working', 'on_break'] as const;
export type AttendanceSnapshotState = (typeof ATTENDANCE_SNAPSHOT_STATES)[number];

/** The `aud` claim every offline grant carries. */
export const GRANT_AUDIENCE = 'syntry-offline-attendance' as const;

/* ── Wire types (verbatim from the shared contract) ─────────────────────── */

/** The authenticated owner ERPat echoes back on every response. */
export type ScopeEchoV1 = { company_key: string; user_id: string; employee_id: string; installation_id: string };

export interface ConfirmedSnapshotV1 {
  state: 'off_shift' | 'working' | 'on_break';
  open_rows: number; // 0, 1 or 2 (2 means ≥2 → ambiguous)
  attendance_id: string | null; // the single open row, else null
  attendance_revision: string | null; // 16 lower-hex
  in_time: string | null; // ISO UTC "YYYY-MM-DDTHH:MM:SS.000Z"
  breaks: string[]; // ISO UTC, compacted break taps of the open row
  status: string | null; // attendance.status of the open row
  last_closed: { attendance_id: string; in_time: string; out_time: string } | null;
  as_of: string; // ERPat server time of the read (ISO UTC, ms)
}

export interface OfflineContextV1 {
  schema_version: 1;
  server_time: string; // ISO UTC with ms
  scope: { company_key: string; user_id: string; employee_id: string; installation_id: string };
  capability: { offline_attendance: boolean; reason: 'disabled' | 'not_ready' | null };
  // grant / keys / policy: always null / [] / null when the capability is off (C2, A8).
  grant: {
    grant_id: string;
    token: string;
    issued_at: string;
    valid_from: string;
    valid_until: string;
    kid: string;
  } | null;
  keys: Array<{ kid: string; kty: 'EC'; crv: 'P-256'; alg: 'ES256'; x: string; y: string }>; // base64url 32-byte coords
  policy: OfflinePolicyV1 | null; // null when capability false
  confirmed: ConfirmedSnapshotV1;
  user: { display_name: string };
}

export interface OfflinePolicyV1 {
  version: string; // 12 lower-hex
  capture_max_age_s: number; // default 259200 (grant lifetime)
  refresh_warn_s: number; // default 86400
  upload_max_age_s: number; // default 259200
  clock_jump_block_s: number; // default 120
  clock_uncertainty_block_s: number; // default 60
  note_required_on_time_out: boolean;
  note_max_length: number; // 500
  // Always 8..64: ERPat reads a missing attendance_max_break_slots row as 24, floors the
  // value at 8 and caps it at 64 (plan amendment C5). The client uses it as sent (C9).
  max_break_entries: number;
  requires_photo: boolean; // false in Release 1
  requires_location: boolean; // false in Release 1
  allowed_actions: Array<'time_in' | 'time_out' | 'break_out' | 'break_in'>;
  max_batch_events: number; // 50
}

/** `capability` of an offline-context or sync-state response. */
export type OfflineCapabilityV1 = OfflineContextV1['capability'];
/** A non-null provisioning `grant`. */
export type OfflineGrantV1 = NonNullable<OfflineContextV1['grant']>;
/** One public key of the provisioning JWKS. */
export type OfflineJwkV1 = OfflineContextV1['keys'][number];

/** `GET sync/state` → `data`. */
export interface SyncStateV1 {
  schema_version: 1;
  server_time: string;
  scope: ScopeEchoV1;
  capability: OfflineContextV1['capability'];
  confirmed: ConfirmedSnapshotV1;
}

export interface SyncRequestV1 {
  schema_version: 1;
  sync_id: string; // new uuid v4 per attempt
  installation_id: string;
  grants: Record<string, string>; // grant_id → JWS for every grant referenced by events
  evidence_refs: Record<string, string>; // always {} in Release 1
  events: EmployeeAttendanceEventV1[]; // 1..50, ascending device_seq, one chain
}

export interface EmployeeAttendanceEventV1 {
  client_event_id: string; // uuid v4 lower-case, minted once
  event_type: 'time_in' | 'time_out' | 'break_out' | 'break_in';
  occurred_at: string; // corrected UTC "YYYY-MM-DDTHH:MM:SS.sssZ", frozen at confirmation
  device_occurred_at: string; // raw device UTC, same format
  device_seq: number; // positive safe integer, per installation
  timezone: string; // IANA, ≤ 64 chars
  // Integers (plan amendment C1): offset_ms = Math.round(offsetMs), uncertainty_ms = Math.ceil(uncertaintyMs).
  clock: { offset_ms: number; calibrated_at: string; uncertainty_ms: number };
  grant_id: string;
  policy_version: string;
  base: {
    attendance_id: string | null;
    attendance_revision: string | null;
    local_shift_id: string | null;
    expected_state: 'off_shift' | 'working' | 'on_break';
    expected_break_count: number;
  };
  depends_on_event_id: string | null;
  note?: string; // time_out only, trimmed, ≤ note_max_length
  captured_connectivity: 'reachable' | 'unreachable' | 'unknown';
  app_version: string; // ≤ 32 chars
}

/** What the device knew about connectivity when the punch was captured. */
export type CapturedConnectivity = EmployeeAttendanceEventV1['captured_connectivity'];

export interface SyncResponseV1 {
  schema_version: 1;
  sync_id: string; // echo
  server_time: string;
  scope: { company_key: string; user_id: string; employee_id: string; installation_id: string };
  results: AttendanceResultV1[]; // one per event, input order
}

/**
 * A decoded `POST sync` response whose `results` are still RAW: the engine
 * decodes each entry with {@link decodeResult} on its own, so one bad entry
 * never discards the answers for the others.
 */
export interface SyncResponseShellV1 {
  schema_version: 1;
  sync_id: string;
  server_time: string;
  scope: ScopeEchoV1;
  results: unknown[];
}

export type AttendanceResultV1 =
  | { client_event_id: string; status: 'accepted'; receipt: FinalReceiptV1 }
  | { client_event_id: string; status: 'rejected'; receipt: FinalReceiptV1 }
  | { client_event_id: string; status: 'duplicate'; receipt: FinalReceiptV1 }
  | { client_event_id: string; status: 'deferred'; code: string; retry_after_ms?: number };

export interface FinalReceiptV1 {
  receipt_id: string;
  client_event_id: string;
  outcome: 'accepted' | 'rejected';
  event_type: 'time_in' | 'time_out' | 'break_out' | 'break_in';
  occurred_at: string; // the event's original occurred_at string, verbatim
  received_at: string; // ISO UTC ms
  attendance_id: string | null;
  attendance_revision: string | null;
  code: string; // 'accepted' or a rejection code below
  message: string; // English, employee-safe
}

/** `GET sync/events/{client_event_id}` → `data` (another employee's id is `not_found`). */
export interface ReceiptLookupV1 {
  schema_version: 1;
  scope: ScopeEchoV1;
  client_event_id: string;
  status: 'final' | 'not_found';
  receipt: FinalReceiptV1 | null;
}

/** The payload of an offline grant (compact ES256 JWS, header `{typ:"JWT",alg:"ES256",kid}`). */
export interface GrantClaimsV1 {
  v: 1;
  gid: string;
  iss: string;
  aud: 'syntry-offline-attendance';
  tnt: string;
  uid: string;
  eid: string;
  ins: string;
  iat: number;
  vfr: number;
  vut: number;
  pv: string;
  acts: AttendanceEventType[];
}

/* ── Result codes ───────────────────────────────────────────────────────── */

const REJECTION_CODE_LIST = [
  'invalid_event',
  'grant_missing',
  'grant_invalid',
  'grant_revoked',
  'grant_expired',
  'too_old',
  'future_event',
  'sequence_conflict',
  'sequence_time_conflict',
  'dependency_rejected',
  'dependency_missing',
  'dependency_mismatch',
  'state_conflict',
  'open_shift_exists',
  'multiple_open_shifts',
  'overlap',
  'out_of_order',
  'no_schedule',
  'date_locked',
  'shift_not_open',
  'on_break',
  'not_on_break',
  'break_limit',
  'before_shift_start',
  'span_too_long',
  'note_required',
  // Not stored by ERPat: synthesized when a resend's content (or owner) does
  // not match the stored event, and it never overwrites that stored receipt.
  'event_id_conflict',
] as const;

const DEFERRAL_CODE_LIST = [
  'lock_busy',
  'lock_unavailable',
  'earlier_event_deferred',
  'state_changed',
  'commit_failed',
  'server_error',
  'time_budget',
] as const;

const WHOLE_REQUEST_ERROR_CODE_LIST = [
  'module_unavailable',
  'forbidden',
  'account_inactive',
  'validation_failed',
  'unsupported_schema',
  'offline_sync_unavailable',
  'rate_limited',
] as const;

export type RejectionCode = (typeof REJECTION_CODE_LIST)[number];
export type DeferralCode = (typeof DEFERRAL_CODE_LIST)[number];
export type WholeRequestErrorCode = (typeof WHOLE_REQUEST_ERROR_CODE_LIST)[number];

/** Final refusals ERPat stores on a receipt (`outcome: 'rejected'`). An unknown code is still final. */
export const REJECTION_CODES: readonly string[] = REJECTION_CODE_LIST;

/**
 * Retry-the-same-id answers (`status: 'deferred'`). Any code arriving on a
 * deferred result is a deferral, listed here or not.
 */
export const DEFERRAL_CODES: readonly string[] = DEFERRAL_CODE_LIST;

/**
 * Envelope error codes that refuse a WHOLE request (no per-event result):
 * they never settle an individual event.
 */
export const WHOLE_REQUEST_ERROR_CODES: readonly string[] = WHOLE_REQUEST_ERROR_CODE_LIST;

export function isRejectionCode(code: string): code is RejectionCode {
  return REJECTION_CODES.includes(code);
}

export function isDeferralCode(code: string): code is DeferralCode {
  return DEFERRAL_CODES.includes(code);
}

export function isAttendanceEventType(value: unknown): value is AttendanceEventType {
  return typeof value === 'string' && (ATTENDANCE_EVENT_TYPES as readonly string[]).includes(value);
}

/* ── Scalar formats ─────────────────────────────────────────────────────── */

const UUID_ANY_CASE_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
/** A UUID exactly as ERPat issues it in grant claims: already lower-case. */
const UUID_LOWER_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/;
const DECIMAL_ID_RE = /^[1-9][0-9]{0,19}$/;
const ISO_UTC_RE = /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2}):(\d{2})(?:\.\d{1,3})?Z$/;
const REVISION_RE = /^[0-9a-f]{16}$/;
const POLICY_VERSION_RE = /^[0-9a-f]{12}$/;
/** A base64url P-256 coordinate: 32 bytes → 43 unpadded characters. */
const COORDINATE_RE = /^[A-Za-z0-9_-]{43}$/;
const COMPACT_JWS_RE = /^[A-Za-z0-9_-]+\.[A-Za-z0-9_-]+\.[A-Za-z0-9_-]+$/;

const MAX_COMPANY_KEY = 128;
const MAX_KID = 64;
const MAX_TOKEN = 8_192;
const MAX_ISSUER = 255;
const MAX_CODE = 64;
const MAX_MESSAGE = 1_000;
const MAX_STATUS = 191;
const MAX_DISPLAY_NAME = 255;

/**
 * `value` is an ISO-8601 UTC timestamp `YYYY-MM-DDTHH:MM:SS[.f{1,3}]Z` naming a
 * real instant. Stricter than `Date.parse`, which silently rolls
 * `2026-02-30` over to March 2 and accepts `24:00:00`: every calendar and clock
 * field must survive the round trip unchanged (ERPat refuses the same values).
 */
export function isIsoUtc(value: unknown): value is string {
  if (typeof value !== 'string') return false;
  const match = ISO_UTC_RE.exec(value);
  if (!match) return false;
  const ms = Date.parse(value);
  if (!Number.isFinite(ms)) return false;
  const date = new Date(ms);
  return (
    date.getUTCFullYear() === Number(match[1]) &&
    date.getUTCMonth() + 1 === Number(match[2]) &&
    date.getUTCDate() === Number(match[3]) &&
    date.getUTCHours() === Number(match[4]) &&
    date.getUTCMinutes() === Number(match[5]) &&
    date.getUTCSeconds() === Number(match[6])
  );
}

/** A UUID of any version and case, lower-cased; `null` when `value` is not one. */
export function normalizeUuid(value: unknown): string | null {
  return typeof value === 'string' && UUID_ANY_CASE_RE.test(value) ? value.toLowerCase() : null;
}

/**
 * A positive decimal id as a string: a positive safe integer, or a canonical
 * positive decimal string (`"812"`, not `"0812"` or `" 812"`). `null` otherwise.
 */
export function normalizeDecimalId(value: unknown): string | null {
  if (typeof value === 'number') return Number.isSafeInteger(value) && value > 0 ? String(value) : null;
  return typeof value === 'string' && DECIMAL_ID_RE.test(value) ? value : null;
}

/* ── Decoder plumbing ───────────────────────────────────────────────────── */

export type DecodeResult<T> = { ok: true; value: T } | { ok: false; reason: string };

/** The reason reported when a decoder is handed something that is not an object. */
const ROOT = '(root)';

/**
 * How a reader reports the first offending key; caught by {@link decodeRoot},
 * never exported. (A declared field, not a constructor parameter property:
 * Node's strip-only TypeScript support refuses parameter properties.)
 */
class ContractViolation extends Error {
  readonly key: string;

  constructor(key: string) {
    super(`offline attendance contract violation at ${key}`);
    this.name = 'ContractViolation';
    this.key = key;
  }
}

function fail(key: string): never {
  throw new ContractViolation(key);
}

type Obj = Record<string, unknown>;
/** Validates/normalizes one value found at `path`, or fails naming `path`. */
type Reader<T> = (value: unknown, path: string) => T;
/** Reads the fields of an object found at `path` ('' at the root). */
type Fields<T> = (record: Obj, path: string) => T;

function isRecord(value: unknown): value is Obj {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function join(path: string, key: string): string {
  return path === '' ? key : `${path}.${key}`;
}

/** `record[key]` when `key` is the record's OWN property, else `undefined` (never `toString` and friends). */
function own(record: Obj, key: string): unknown {
  return Object.prototype.hasOwnProperty.call(record, key) ? record[key] : undefined;
}

/** A REQUIRED key (it may hold `null` only when `read` allows null). */
function req<T>(record: Obj, key: string, path: string, read: Reader<T>): T {
  const at = join(path, key);
  const value = own(record, key);
  if (value === undefined) fail(at);
  return read(value, at);
}

/** An OPTIONAL key: absent or `null` reads as `undefined`. */
function opt<T>(record: Obj, key: string, path: string, read: Reader<T>): T | undefined {
  const value = own(record, key);
  return value === undefined || value === null ? undefined : read(value, join(path, key));
}

function decodeRoot<T>(value: unknown, fields: Fields<T>): DecodeResult<T> {
  if (!isRecord(value)) return { ok: false, reason: ROOT };
  try {
    return { ok: true, value: fields(value, '') };
  } catch (err) {
    // A ContractViolation is the expected "malformed" outcome and carries the
    // offending key. Anything else is a bug in this module and must surface
    // instead of reading as "the server sent a bad payload".
    if (err instanceof ContractViolation) return { ok: false, reason: err.key };
    throw err;
  }
}

/* ── Readers ────────────────────────────────────────────────────────────── */

const isoReader: Reader<string> = (value, path) => (isIsoUtc(value) ? value : fail(path));
const uuidReader: Reader<string> = (value, path) => normalizeUuid(value) ?? fail(path);
const idReader: Reader<string> = (value, path) => normalizeDecimalId(value) ?? fail(path);
const boolReader: Reader<boolean> = (value, path) => (typeof value === 'boolean' ? value : fail(path));
const arrayReader: Reader<unknown[]> = (value, path) => (Array.isArray(value) ? value : fail(path));

/** A result/receipt code: 1–64 characters with no surrounding whitespace. */
const codeReader: Reader<string> = (value, path) =>
  typeof value === 'string' && value.length > 0 && value.length <= MAX_CODE && value.trim() === value
    ? value
    : fail(path);

const jwsReader: Reader<string> = (value, path) =>
  typeof value === 'string' && value.length <= MAX_TOKEN && COMPACT_JWS_RE.test(value) ? value : fail(path);

/**
 * A deferred result's `retry_after_ms`: a finite, non-negative number. A SHAPE
 * check only — the value comes back as sent, neither rounded nor bounded; its
 * consumer caps it at one hour with `boundRetryAfterMs()` from
 * `services/api/offline-attendance.api.ts` (plan amendment C16).
 */
const retryAfterReader: Reader<number> = (value, path) =>
  typeof value === 'number' && Number.isFinite(value) && value >= 0 ? value : fail(path);

/** Any string up to `max` code units; with `nonBlank`, it must hold more than whitespace. */
function textReader(max: number, nonBlank: boolean): Reader<string> {
  return (value, path) =>
    typeof value === 'string' && value.length <= max && (!nonBlank || value.trim().length > 0) ? value : fail(path);
}

function patternReader(re: RegExp): Reader<string> {
  return (value, path) => (typeof value === 'string' && re.test(value) ? value : fail(path));
}

/** A safe integer in [min, max]. */
function intReader(min: number, max: number = Number.MAX_SAFE_INTEGER): Reader<number> {
  return (value, path) =>
    typeof value === 'number' && Number.isSafeInteger(value) && value >= min && value <= max ? value : fail(path);
}

function enumReader<const T extends string>(values: readonly T[]): Reader<T> {
  return (value, path) =>
    typeof value === 'string' && (values as readonly string[]).includes(value) ? (value as T) : fail(path);
}

function literalReader<const T extends string | number>(expected: T): Reader<T> {
  return (value, path) => (value === expected ? expected : fail(path));
}

function nullable<T>(read: Reader<T>): Reader<T | null> {
  return (value, path) => (value === null ? null : read(value, path));
}

/** An array whose every element passes `read` (element paths are `path[i]`). */
function listOf<T>(read: Reader<T>): Reader<T[]> {
  return (value, path) => arrayReader(value, path).map((item, index) => read(item, `${path}[${index}]`));
}

/** An object whose fields are read by `fields`. */
function obj<T>(fields: Fields<T>): Reader<T> {
  return (value, path) => fields(isRecord(value) ? value : fail(path), path);
}

const companyKeyReader = textReader(MAX_COMPANY_KEY, true);
const kidReader = textReader(MAX_KID, true);
const revisionReader = patternReader(REVISION_RE);
const policyVersionReader = patternReader(POLICY_VERSION_RE);

/** Exactly `null`. */
const nullReader: Reader<null> = (value, path) => (value === null ? null : fail(path));

/** Grant claims (plan amendment C4): a lower-case UUID / a canonical decimal STRING, never normalized. */
const canonicalUuidReader = patternReader(UUID_LOWER_RE);
const canonicalDecimalReader = patternReader(DECIMAL_ID_RE);

/** A list of distinct clock actions; a repeated or unknown action fails at its index. */
const actionsReader: Reader<AttendanceEventType[]> = (value, path) => {
  const actions = listOf(enumReader(ATTENDANCE_EVENT_TYPES))(value, path);
  actions.forEach((action, index) => {
    if (actions.indexOf(action) !== index) fail(`${path}[${index}]`);
  });
  return actions;
};

/* ── Object fields ──────────────────────────────────────────────────────── */

const scopeFields: Fields<ScopeEchoV1> = (o, p) => ({
  company_key: req(o, 'company_key', p, companyKeyReader),
  user_id: req(o, 'user_id', p, idReader),
  employee_id: req(o, 'employee_id', p, idReader),
  installation_id: req(o, 'installation_id', p, uuidReader),
});

/** The reasons an OFF capability names; any other (or none) reads as `not_ready`. */
const CAPABILITY_OFF_REASONS = ['disabled', 'not_ready'] as const;

/**
 * `offline_attendance` must be a boolean — on and off cannot be told apart
 * otherwise. ON must say `reason: null`: an "on" answer that also gives a reason
 * for being off is contradictory and refused. OFF always decodes: `disabled` /
 * `not_ready` are kept, and a missing, null or unknown reason — one a later
 * ERPat might add — reads as `not_ready` (FE-A2, extending plan amendment C2,
 * which covers the grant, policy and keys, to the reason), because refusing an
 * answer that says capture is off would leave a stored ENABLED context
 * authorizing capture. The decoded value always keeps
 * `offline_attendance === true` ⇔ `reason === null`.
 */
const capabilityFields: Fields<OfflineCapabilityV1> = (o, p) => {
  const offline_attendance = req(o, 'offline_attendance', p, boolReader);
  if (offline_attendance) {
    return { offline_attendance, reason: req(o, 'reason', p, nullReader) };
  }
  const reason = own(o, 'reason');
  const known = CAPABILITY_OFF_REASONS.find((offReason) => offReason === reason);
  return { offline_attendance, reason: known ?? 'not_ready' };
};

/**
 * The last closed shift. One that ends BEFORE it starts reads as `null` — no
 * last closed shift — rather than refusing the whole snapshot (plan amendment
 * C3; ERPat drops such a row itself). A shift that ends the moment it starts is
 * kept, and unreadable times are still refused.
 */
const lastClosedFields: Fields<NonNullable<ConfirmedSnapshotV1['last_closed']> | null> = (o, p) => {
  const lastClosed = {
    attendance_id: req(o, 'attendance_id', p, idReader),
    in_time: req(o, 'in_time', p, isoReader),
    out_time: req(o, 'out_time', p, isoReader),
  };
  return Date.parse(lastClosed.out_time) < Date.parse(lastClosed.in_time) ? null : lastClosed;
};

/**
 * A confirmed snapshot. Beyond the field types, the row count must agree with
 * the rest (ERPat builds them that way): no open row ⇒ `off_shift` with no id,
 * revision, in_time or breaks; exactly one ⇒ a working/on-break row with its
 * id and revision, `on_break` exactly when the break count is odd. Its
 * `in_time` may be `null` (plan amendment C3): ERPat could not read the row's
 * clock-in, and the projection treats the shift start as unknown — one legacy
 * row must not make every provisioning and state refresh undecodable. Two (or
 * more) open rows are ambiguous and carry no row details.
 */
const snapshotFields: Fields<ConfirmedSnapshotV1> = (o, p) => {
  const state = req(o, 'state', p, enumReader(ATTENDANCE_SNAPSHOT_STATES));
  const open_rows = req(o, 'open_rows', p, intReader(0, 2));
  const attendance_id = req(o, 'attendance_id', p, nullable(idReader));
  const attendance_revision = req(o, 'attendance_revision', p, nullable(revisionReader));
  const in_time = req(o, 'in_time', p, nullable(isoReader));
  const breaks = req(o, 'breaks', p, listOf(isoReader));
  const status = req(o, 'status', p, nullable(textReader(MAX_STATUS, false)));
  const last_closed = req(o, 'last_closed', p, nullable(obj(lastClosedFields)));
  const as_of = req(o, 'as_of', p, isoReader);

  if (open_rows === 0) {
    if (state !== 'off_shift') fail(join(p, 'state'));
    if (attendance_id !== null) fail(join(p, 'attendance_id'));
    if (attendance_revision !== null) fail(join(p, 'attendance_revision'));
    if (in_time !== null) fail(join(p, 'in_time'));
    if (breaks.length > 0) fail(join(p, 'breaks'));
  } else if (open_rows === 1) {
    if (state === 'off_shift') fail(join(p, 'state'));
    if (attendance_id === null) fail(join(p, 'attendance_id'));
    if (attendance_revision === null) fail(join(p, 'attendance_revision'));
    if ((state === 'on_break') !== (breaks.length % 2 === 1)) fail(join(p, 'breaks'));
  }

  return { state, open_rows, attendance_id, attendance_revision, in_time, breaks, status, last_closed, as_of };
};

const grantFields: Fields<OfflineGrantV1> = (o, p) => {
  const grant = {
    grant_id: req(o, 'grant_id', p, uuidReader),
    token: req(o, 'token', p, jwsReader),
    issued_at: req(o, 'issued_at', p, isoReader),
    valid_from: req(o, 'valid_from', p, isoReader),
    valid_until: req(o, 'valid_until', p, isoReader),
    kid: req(o, 'kid', p, kidReader),
  };
  if (Date.parse(grant.valid_until) <= Date.parse(grant.valid_from)) fail(join(p, 'valid_until'));
  return grant;
};

const jwkFields: Fields<OfflineJwkV1> = (o, p) => ({
  kid: req(o, 'kid', p, kidReader),
  kty: req(o, 'kty', p, literalReader('EC')),
  crv: req(o, 'crv', p, literalReader('P-256')),
  alg: req(o, 'alg', p, literalReader('ES256')),
  x: req(o, 'x', p, patternReader(COORDINATE_RE)),
  y: req(o, 'y', p, patternReader(COORDINATE_RE)),
});

/** The JWKS: every key a P-256/ES256 EC key, every `kid` distinct. */
const keysReader: Reader<OfflineJwkV1[]> = (value, path) => {
  const keys = listOf(obj(jwkFields))(value, path);
  keys.forEach((key, index) => {
    if (keys.findIndex((other) => other.kid === key.kid) !== index) fail(`${path}[${index}].kid`);
  });
  return keys;
};

const policyFields: Fields<OfflinePolicyV1> = (o, p) => ({
  version: req(o, 'version', p, policyVersionReader),
  capture_max_age_s: req(o, 'capture_max_age_s', p, intReader(1)),
  refresh_warn_s: req(o, 'refresh_warn_s', p, intReader(0)),
  upload_max_age_s: req(o, 'upload_max_age_s', p, intReader(1)),
  clock_jump_block_s: req(o, 'clock_jump_block_s', p, intReader(1)),
  clock_uncertainty_block_s: req(o, 'clock_uncertainty_block_s', p, intReader(1)),
  note_required_on_time_out: req(o, 'note_required_on_time_out', p, boolReader),
  note_max_length: req(o, 'note_max_length', p, intReader(1)),
  max_break_entries: req(o, 'max_break_entries', p, intReader(1)),
  requires_photo: req(o, 'requires_photo', p, boolReader),
  requires_location: req(o, 'requires_location', p, boolReader),
  allowed_actions: req(o, 'allowed_actions', p, actionsReader),
  max_batch_events: req(o, 'max_batch_events', p, intReader(1)),
});

const userFields: Fields<OfflineContextV1['user']> = (o, p) => ({
  display_name: req(o, 'display_name', p, textReader(MAX_DISPLAY_NAME, false)),
});

/**
 * A provisioning response. A capability that is ON must carry a grant, a policy
 * and at least one key — an enabled-but-incomplete answer is refused. One that
 * is OFF never has them READ: they decode as `grant: null`, `policy: null`,
 * `keys: []` whatever arrives (present, absent, malformed), which is exactly
 * what ERPat sends (backend amendment A8). The client must not use them, and
 * refusing a capture-is-off answer over them would fail open (plan amendment C2).
 */
const contextFields: Fields<OfflineContextV1> = (o, p) => {
  const schema_version = req(o, 'schema_version', p, literalReader(OFFLINE_SCHEMA_VERSION));
  const server_time = req(o, 'server_time', p, isoReader);
  const scope = req(o, 'scope', p, obj(scopeFields));
  const capability = req(o, 'capability', p, obj(capabilityFields));
  const enabled = capability.offline_attendance;
  let grant: OfflineGrantV1 | null = null;
  let keys: OfflineJwkV1[] = [];
  let policy: OfflinePolicyV1 | null = null;
  if (enabled) {
    grant = req(o, 'grant', p, nullable(obj(grantFields)));
    keys = req(o, 'keys', p, keysReader);
    policy = req(o, 'policy', p, nullable(obj(policyFields)));
  }
  const confirmed = req(o, 'confirmed', p, obj(snapshotFields));
  const user = req(o, 'user', p, obj(userFields));

  if (enabled) {
    if (grant === null) fail(join(p, 'grant'));
    if (policy === null) fail(join(p, 'policy'));
    if (keys.length === 0) fail(join(p, 'keys'));
  }

  return { schema_version, server_time, scope, capability, grant, keys, policy, confirmed, user };
};

const syncStateFields: Fields<SyncStateV1> = (o, p) => ({
  schema_version: req(o, 'schema_version', p, literalReader(OFFLINE_SCHEMA_VERSION)),
  server_time: req(o, 'server_time', p, isoReader),
  scope: req(o, 'scope', p, obj(scopeFields)),
  capability: req(o, 'capability', p, obj(capabilityFields)),
  confirmed: req(o, 'confirmed', p, obj(snapshotFields)),
});

/**
 * A final receipt. `accepted` ⇔ `code === 'accepted'`, and an accepted receipt
 * names the attendance row and its new revision.
 */
const receiptFields: Fields<FinalReceiptV1> = (o, p) => {
  const receipt = {
    receipt_id: req(o, 'receipt_id', p, uuidReader),
    client_event_id: req(o, 'client_event_id', p, uuidReader),
    outcome: req(o, 'outcome', p, enumReader(['accepted', 'rejected'] as const)),
    event_type: req(o, 'event_type', p, enumReader(ATTENDANCE_EVENT_TYPES)),
    occurred_at: req(o, 'occurred_at', p, isoReader),
    received_at: req(o, 'received_at', p, isoReader),
    attendance_id: req(o, 'attendance_id', p, nullable(idReader)),
    attendance_revision: req(o, 'attendance_revision', p, nullable(revisionReader)),
    code: req(o, 'code', p, codeReader),
    message: req(o, 'message', p, textReader(MAX_MESSAGE, false)),
  };
  if (receipt.outcome === 'accepted') {
    if (receipt.code !== 'accepted') fail(join(p, 'code'));
    if (receipt.attendance_id === null) fail(join(p, 'attendance_id'));
    if (receipt.attendance_revision === null) fail(join(p, 'attendance_revision'));
  } else if (receipt.code === 'accepted') {
    fail(join(p, 'code'));
  }
  return receipt;
};

/**
 * One per-event result. `accepted` needs an accepted receipt, `rejected` a
 * rejected one, `duplicate` either (it replays the original); the receipt must
 * be for the result's own event. `deferred` needs a code (any code — an unknown
 * one is still a deferral) and may carry a finite, non-negative `retry_after_ms`.
 */
const resultFields: Fields<AttendanceResultV1> = (o, p) => {
  const client_event_id = req(o, 'client_event_id', p, uuidReader);
  const status = req(o, 'status', p, enumReader(['accepted', 'rejected', 'duplicate', 'deferred'] as const));

  if (status === 'deferred') {
    const code = req(o, 'code', p, codeReader);
    const retry_after_ms = opt(o, 'retry_after_ms', p, retryAfterReader);
    return retry_after_ms === undefined
      ? { client_event_id, status, code }
      : { client_event_id, status, code, retry_after_ms };
  }

  const receipt = req(o, 'receipt', p, obj(receiptFields));
  if (status !== 'duplicate' && receipt.outcome !== status) fail(join(p, 'receipt.outcome'));
  if (receipt.client_event_id !== client_event_id) fail(join(p, 'receipt.client_event_id'));
  return { client_event_id, status, receipt };
};

const syncResponseFields: Fields<SyncResponseShellV1> = (o, p) => ({
  schema_version: req(o, 'schema_version', p, literalReader(OFFLINE_SCHEMA_VERSION)),
  sync_id: req(o, 'sync_id', p, uuidReader),
  server_time: req(o, 'server_time', p, isoReader),
  scope: req(o, 'scope', p, obj(scopeFields)),
  // Copied, not aliased; the entries themselves stay raw (see SyncResponseShellV1).
  results: req(o, 'results', p, arrayReader).slice(),
});

/** `final` carries this event's receipt; `not_found` carries none. */
const receiptLookupFields: Fields<ReceiptLookupV1> = (o, p) => {
  const schema_version = req(o, 'schema_version', p, literalReader(OFFLINE_SCHEMA_VERSION));
  const scope = req(o, 'scope', p, obj(scopeFields));
  const client_event_id = req(o, 'client_event_id', p, uuidReader);
  const status = req(o, 'status', p, enumReader(['final', 'not_found'] as const));
  const receipt = req(o, 'receipt', p, nullable(obj(receiptFields)));
  if (status === 'final') {
    if (receipt === null) fail(join(p, 'receipt'));
    if (receipt.client_event_id !== client_event_id) fail(join(p, 'receipt.client_event_id'));
  } else if (receipt !== null) {
    fail(join(p, 'receipt'));
  }
  return { schema_version, scope, client_event_id, status, receipt };
};

/**
 * Grant claims: exact audience, a validity window that is not empty or
 * inverted, and CANONICAL ids (plan amendment C4). ERPat's verifier compares
 * `uid`, `eid`, `gid` and `ins` verbatim (backend amendment A10), so `uid` /
 * `eid` must already be canonical decimal strings — a number is refused — and
 * `gid` / `ins` already lower-case UUIDs. Nothing is normalized: a claim ERPat
 * would refuse at upload is refused here, before any punch is captured under it.
 */
const grantClaimsFields: Fields<GrantClaimsV1> = (o, p) => {
  const claims = {
    v: req(o, 'v', p, literalReader(1)),
    gid: req(o, 'gid', p, canonicalUuidReader),
    iss: req(o, 'iss', p, textReader(MAX_ISSUER, true)),
    aud: req(o, 'aud', p, literalReader(GRANT_AUDIENCE)),
    tnt: req(o, 'tnt', p, companyKeyReader),
    uid: req(o, 'uid', p, canonicalDecimalReader),
    eid: req(o, 'eid', p, canonicalDecimalReader),
    ins: req(o, 'ins', p, canonicalUuidReader),
    iat: req(o, 'iat', p, intReader(0)),
    vfr: req(o, 'vfr', p, intReader(0)),
    vut: req(o, 'vut', p, intReader(0)),
    pv: req(o, 'pv', p, policyVersionReader),
    acts: req(o, 'acts', p, actionsReader),
  };
  if (claims.vut <= claims.vfr) fail(join(p, 'vut'));
  return claims;
};

/* ── Public decoders ────────────────────────────────────────────────────── */

/**
 * A confirmed snapshot — what `GET offline-context` and `GET sync/state` carry
 * as `confirmed`. ALSO the decoder of data AT REST: `attendance-storage.ts`
 * re-decodes every snapshot it stored when it loads one back. Within v1 this
 * shape may only be LOOSENED: a stricter rule would turn snapshots an older
 * build stored into `corrupt` reads. Tightening it is a schema-version change.
 */
export function decodeConfirmedSnapshot(value: unknown): DecodeResult<ConfirmedSnapshotV1> {
  return decodeRoot(value, snapshotFields);
}

/** `GET offline-context` → `data`. */
export function decodeOfflineContext(value: unknown): DecodeResult<OfflineContextV1> {
  return decodeRoot(value, contextFields);
}

/** `GET sync/state` → `data`. */
export function decodeSyncState(value: unknown): DecodeResult<SyncStateV1> {
  return decodeRoot(value, syncStateFields);
}

/**
 * A final receipt — the `receipt` of a `POST sync` result and of a receipt
 * lookup. ALSO the decoder of data AT REST: `attendance-storage.ts` re-decodes
 * every receipt it stored when it reads one back (an event's settlement and the
 * chain listing). Within v1 this shape may only be LOOSENED: a stricter rule
 * would turn receipts an older build stored into `corrupt` reads. Tightening it
 * is a schema-version change.
 */
export function decodeFinalReceipt(value: unknown): DecodeResult<FinalReceiptV1> {
  return decodeRoot(value, receiptFields);
}

/** One entry of `POST sync` → `data.results`. */
export function decodeResult(value: unknown): DecodeResult<AttendanceResultV1> {
  return decodeRoot(value, resultFields);
}

/**
 * `POST sync` → `data`, with `results` left undecoded: decode each entry with
 * {@link decodeResult} so one bad entry never discards the others.
 */
export function decodeSyncResponse(value: unknown): DecodeResult<SyncResponseShellV1> {
  return decodeRoot(value, syncResponseFields);
}

/** `GET sync/events/{client_event_id}` → `data`. */
export function decodeReceiptLookup(value: unknown): DecodeResult<ReceiptLookupV1> {
  return decodeRoot(value, receiptLookupFields);
}

/**
 * The parsed JSON payload of a grant JWS. Shape only — canonical, never
 * normalized (see `grantClaimsFields`): the signature, the binding to the scope
 * and the validity window are `grant-verify.ts`'s job.
 */
export function decodeGrantClaims(value: unknown): DecodeResult<GrantClaimsV1> {
  return decodeRoot(value, grantClaimsFields);
}
